import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [pending2fa, setPending2fa] = useState(null); // {challenge_id}
  const [totpCode, setTotpCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/", { replace: true }); return; }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId }, { withCredentials: true });
        if (data.pending_2fa && data.challenge_id) {
          setPending2fa({ challenge_id: data.challenge_id });
          window.history.replaceState(null, "", window.location.pathname);
          return;
        }
        await refresh();
        toast.success(`Welcome ${data.name || data.email}`);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      } catch (e) {
        toast.error("Sign-in failed");
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  const complete2fa = async () => {
    if (!/^\d{6}$/.test(totpCode)) { toast.error("Enter 6-digit authenticator code"); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/2fa/complete", { challenge_id: pending2fa.challenge_id, code: totpCode }, { withCredentials: true });
      await refresh();
      toast.success(`Welcome ${data.name || data.email}`);
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "2FA verification failed");
    } finally {
      setVerifying(false);
    }
  };

  if (pending2fa) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-surface)]" data-testid="auth-2fa-prompt">
        <div className="w-full max-w-sm px-6">
          <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-2">◈ 2FA REQUIRED</div>
          <div className="font-serif-elite text-2xl text-white mb-3">Authenticator code</div>
          <input
            data-testid="oauth-totp-input"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => e.key === "Enter" && complete2fa()}
            placeholder="6-digit TOTP"
            autoFocus
            className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-3 py-2.5 text-lg font-mono-data text-white tracking-widest focus:outline-none focus:border-[color:var(--border-focus)]"
          />
          <button
            data-testid="oauth-totp-verify-btn"
            onClick={complete2fa}
            disabled={verifying}
            className="w-full mt-3 bg-white text-black py-2.5 text-xs font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors"
          >{verifying ? "VERIFYING…" : "COMPLETE SIGN-IN →"}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-surface)]" data-testid="auth-callback">
      <div className="text-center">
        <div className="font-serif-elite text-2xl text-white mb-2">Signing you in…</div>
        <div className="text-xs font-mono-data text-[color:var(--text-muted)]">Authenticating with Emergent Google Auth</div>
      </div>
    </div>
  );
}
